import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import StorageIcon from '@material-ui/icons/Storage';
import LayersIcon from '@material-ui/icons/Layers';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import ExtensionIcon from '@material-ui/icons/Extension';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import TimelineIcon from '@material-ui/icons/Timeline';
import ExploreIcon from '@material-ui/icons/Explore';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import CampaignIcon from '@material-ui/icons/RecordVoiceOver';
import BuildIcon from '@material-ui/icons/Build';
import SecurityIcon from '@material-ui/icons/Security';
import type { BasicPermission } from '@backstage/plugin-permission-common';
import { PermissionSidebarItem } from '@internal/plugin-rbac';
import {
  activityReadPermission,
  addonReadPermission,
  aiUsePermission,
  catalogPageReadPermission,
  clusterReadPermission,
  costReadPermission,
  credentialsReadPermission,
  databaseReadPermission,
  environmentReadPermission,
  rbacReadPermission,
  scorecardReadPermission,
  selfServiceReadPermission,
  workflowReadPermission,
  workloadReadPermission,
} from '@internal/plugin-rbac-common';
import HistoryIcon from '@material-ui/icons/History';
import AppsIcon from '@material-ui/icons/Apps';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import ChatIcon from '@material-ui/icons/Chat';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { githubAuthApiRef, useApi } from "@backstage/core-plugin-api";

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/database" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const LogoutButton = () => {
    const githubAuth = useApi(githubAuthApiRef);

    const handleLogout = async () => {
        // This clears the GitHub session from Backstage
        await githubAuth.signOut();
        // Refresh the page to return to the sign-in screen
        window.location.reload();
    };

    return (
            <SidebarItem
                    icon={ExitToAppIcon}
                    text="Sign Out"
                    onClick={handleLogout}
            />
    );
};

/**
 * A sidebar entry that is not drawn at all unless the person may use it.
 *
 * Hidden rather than greyed out — a disabled row still advertises a feature
 * they were not given. Enforcement is on the route and in the backend; this
 * only keeps the navigation honest about what is reachable.
 */
const Gated = ({
  permission,
  children,
}: PropsWithChildren<{ permission: BasicPermission }>) => (
  <PermissionSidebarItem permission={permission}>
    {children}
  </PermissionSidebarItem>
);

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <Gated permission={aiUsePermission}>
          <SidebarItem icon={ChatIcon} to="ai" text="Pocteo AI" />
        </Gated>
        <Gated permission={selfServiceReadPermission}>
          <SidebarItem icon={AppsIcon} to="self-service" text="Self-Service" />
        </Gated>
        <Gated permission={databaseReadPermission}>
          <SidebarItem icon={StorageIcon} to="database" text="Databases" />
        </Gated>
        <Gated permission={workloadReadPermission}>
          <SidebarItem icon={CloudUploadIcon} to="workloads" text="Workloads" />
        </Gated>
        <Gated permission={environmentReadPermission}>
          <SidebarItem icon={LayersIcon} to="environments" text="Environments" />
        </Gated>
        <Gated permission={clusterReadPermission}>
          <SidebarItem icon={DeviceHubIcon} to="clusters" text="Clusters" />
        </Gated>
        <Gated permission={workflowReadPermission}>
          <SidebarItem icon={TimelineIcon} to="pipelines" text="Workflows" />
        </Gated>
        <Gated permission={addonReadPermission}>
          <SidebarItem icon={ExtensionIcon} to="addons" text="Add-ons" />
        </Gated>
        <Gated permission={costReadPermission}>
          <SidebarItem icon={MonetizationOnIcon} to="cost" text="Cost" />
        </Gated>
        <Gated permission={credentialsReadPermission}>
          <SidebarItem icon={VpnKeyIcon} to="credentials" text="Credentials" />
        </Gated>
        <SidebarDivider />
        <Gated permission={catalogPageReadPermission}>
          <SidebarItem icon={LibraryBooksIcon} to="catalog-overview" text="Catalog" />
        </Gated>
        <Gated permission={scorecardReadPermission}>
          <SidebarItem icon={EmojiEventsIcon} to="scorecards" text="Scorecards" />
        </Gated>
        <SidebarItem icon={ExploreIcon} to="explore" text="Explore" />
        <SidebarItem icon={CampaignIcon} to="announcements" text="Announcements" />
        <Gated permission={activityReadPermission}>
          <SidebarItem icon={HistoryIcon} to="activity" text="Activity" />
        </Gated>
        <Gated permission={rbacReadPermission}>
          <SidebarItem icon={SecurityIcon} to="rbac" text="Access" />
        </Gated>
        <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
        <SidebarDivider />
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
        <LogoutButton />
    </Sidebar>
    {children}
  </SidebarPage>
);
